import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import { MagnifyingGlass, BookOpenText, X } from "@phosphor-icons/react";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function KitchenHelp() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [heroUrl, setHeroUrl] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get("/public/kitchen-help")
      .then((r) => {
        setItems(r.data.items || []);
        setCategories(r.data.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/settings").then((r) => setHeroUrl(r.data?.kitchen_help_hero_url || "")).catch(() => {});
  }, []);

  // Deep-link anchor: on load, if URL has #slug, scroll to it.
  useEffect(() => {
    if (loading || items.length === 0) return;
    const hash = (window.location.hash || "").replace("#", "").toLowerCase();
    if (!hash) return;
    // Give the DOM a tick to render
    setTimeout(() => {
      const el = document.getElementById(`term-${hash}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-rf-amber");
        setTimeout(() => el.classList.remove("ring-2", "ring-rf-amber"), 2400);
      }
    }, 120);
  }, [loading, items]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((t) => {
      if (cat && t.category !== cat) return false;
      if (!query) return true;
      const hay = [
        t.term,
        t.definition,
        ...(t.also_known_as || []),
        ...(t.related_terms || []),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(query);
    });
  }, [items, q, cat]);

  // Report typed searches that find nothing so admins can fill the gap.
  useEffect(() => {
    const term = q.trim();
    if (loading || cat || filtered.length > 0 || term.length < 3) return undefined;
    const id = setTimeout(() => { api.post("/public/kitchen-help/missing", { term }).catch(() => {}); }, 1800);
    return () => clearTimeout(id);
  }, [q, cat, filtered.length, loading]);

  const grouped = useMemo(() => {
    const map = {};
    for (const t of filtered) {
      const letter = (t.term?.[0] || "#").toUpperCase();
      const key = /[A-Z]/.test(letter) ? letter : "#";
      (map[key] ||= []).push(t);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.term || "").localeCompare(b.term || ""));
    }
    return map;
  }, [filtered]);

  const activeLetters = new Set(Object.keys(grouped));

  return (
    <div className="min-h-screen bg-rf-paper" data-testid="kitchen-help-page">
      <PageMeta
        title="Kitchen Help — Cooking terms, cuts, techniques & tools glossary"
        description="A friendly UK-first glossary of cooking terms: abbreviations, ingredient aliases (courgette ↔ zucchini), cuts of meat, techniques, tools and measures. Search, filter and learn as you cook."
        canonical="/kitchen-help"
        keywords="cooking glossary UK, kitchen terminology, cooking terms explained, ingredient aliases, courgette zucchini aubergine, cuts of meat guide, cooking techniques glossary, kitchen tools glossary, culinary dictionary, cooking abbreviations, chef terminology, cooking measures conversion, Recipe Filer Kitchen Help"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Recipe Filer", item: "https://recipefiler.com/" },
              { "@type": "ListItem", position: 2, name: "Kitchen Help", item: "https://recipefiler.com/kitchen-help" },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Kitchen Help — Cooking terms glossary",
            url: "https://recipefiler.com/kitchen-help",
            description: "A UK-first glossary of cooking terms, abbreviations, ingredient aliases, cuts of meat, techniques, tools and measures.",
            isPartOf: { "@type": "WebSite", name: "Recipe Filer", url: "https://recipefiler.com" },
            inLanguage: "en-GB",
          },
        ]}
      />

      {/* Hero — side-by-side layout matching /historical so the title never
          overlaps the image. Left: overline + H1 + description + search.
          Right: admin-uploadable hero photo, rounded, subtle ring. */}
      <div className="bg-gradient-to-br from-amber-900/90 via-rf-ink to-rf-ink text-rf-cream py-12 px-6" data-testid="kh-hero">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-8 items-center">
          <div className={heroUrl ? "lg:col-span-3" : "lg:col-span-5"}>
            <div className="flex items-center gap-3 mb-3">
              <BookOpenText size={26} weight="duotone" className="text-rf-amber" />
              <p className="overline !text-rf-amber !text-xs">Kitchen Help</p>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
              The Recipe Filer <span className="text-rf-amber">culinary glossary</span>.
            </h1>
            <p className="mt-5 text-lg opacity-90 max-w-2xl leading-relaxed">
              Cooking terms, abbreviations, cuts of meat, ingredient aliases (courgette / zucchini),
              techniques, tools and measures — all in one place. Written in British English, mums-in-the-kitchen tested.
            </p>

            {/* Search + Filter */}
            <div className="mt-8 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-rf-ink/50" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search a term, technique or ingredient…"
                  className="w-full pl-12 pr-10 py-3 rounded-full bg-white text-rf-ink placeholder:text-rf-ink/40 shadow-sm focus:outline-none focus:ring-2 focus:ring-rf-amber"
                  data-testid="kh-search-input"
                />
                {q && (
                  <button
                    onClick={() => setQ("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-rf-ink/40 hover:text-rf-ink"
                    data-testid="kh-search-clear"
                    aria-label="Clear search"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              <select
                value={cat}
                onChange={(e) => setCat(e.target.value)}
                className="rounded-full bg-white text-rf-ink border-0 px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-rf-amber"
                data-testid="kh-category-filter"
              >
                <option value="">All categories</option>
                {(categories || []).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          {heroUrl && (
            <div className="lg:col-span-2" data-testid="kh-hero-image">
              <img
                src={heroUrl}
                alt="Kitchen Help — a warm British kitchen scene"
                loading="eager"
                fetchPriority="high"
                className="w-full aspect-[3/2] rounded-3xl object-cover shadow-2xl ring-1 ring-rf-cream/10"
              />
            </div>
          )}
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-6 pb-10">

        {/* A–Z jumper */}
        <div className="mt-6 flex flex-wrap gap-1.5" data-testid="kh-az-jumper">
          {LETTERS.map((L) => {
            const active = activeLetters.has(L);
            return (
              <a
                key={L}
                href={active ? `#letter-${L}` : undefined}
                onClick={(e) => {
                  if (!active) { e.preventDefault(); return; }
                  const el = document.getElementById(`letter-${L}`);
                  if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); }
                }}
                className={`inline-flex items-center justify-center w-10 h-10 sm:w-8 sm:h-8 rounded-full text-sm font-bold transition ${active ? "bg-rf-orange text-white hover:bg-rf-ink" : "bg-rf-ink/5 text-rf-ink/30 cursor-not-allowed"}`}
                data-testid={`kh-az-${L}`}
              >
                {L}
              </a>
            );
          })}
        </div>

        <p className="mt-4 text-sm text-rf-ink/50" data-testid="kh-count">
          {loading ? "Loading…" : `${filtered.length} of ${items.length} term${items.length === 1 ? "" : "s"}`}
        </p>
      </section>

      {/* List */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-24">
        {loading && (
          <div className="text-center py-24 text-rf-ink/50">Loading glossary…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-24 text-rf-ink/60" data-testid="kh-no-results">
            <p className="text-lg font-medium">No terms matched.</p>
            <p className="text-sm mt-2">Try clearing the search or picking a different category.</p>
          </div>
        )}
        {!loading && Object.keys(grouped).sort().map((letter) => (
          <div key={letter} className="mb-14" id={`letter-${letter}`}>
            <div className="flex items-baseline gap-4 mb-5 sticky top-0 bg-rf-paper/95 backdrop-blur py-2 z-10 border-b border-rf-ink/10">
              <h2 className="font-display text-4xl font-black text-rf-orange" data-testid={`kh-letter-${letter}`}>
                {letter}
              </h2>
              <span className="text-sm text-rf-ink/40">{grouped[letter].length} term{grouped[letter].length === 1 ? "" : "s"}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {grouped[letter].map((t) => (
                <article
                  key={t.term_id}
                  id={`term-${t.slug}`}
                  data-testid={`kh-term-${t.slug}`}
                  className="bg-white rounded-2xl p-5 border border-rf-ink/5 hover:border-rf-amber/50 transition scroll-mt-24"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-display text-xl font-black text-rf-ink">
                      <Link to={`/kitchen-help/${t.slug}`} className="hover:text-rf-orange">
                        {t.term}
                      </Link>
                    </h3>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rf-amber/15 text-rf-orange text-[10px] font-bold uppercase tracking-wider shrink-0">
                      {t.category}
                    </span>
                  </div>
                  <p className="text-sm text-rf-ink/75 leading-relaxed">{t.definition}</p>
                  {t.also_known_as && t.also_known_as.length > 0 && (
                    <p className="mt-3 text-xs text-rf-ink/50">
                      <span className="font-bold uppercase tracking-wider text-rf-ink/40">Also:</span>{" "}
                      {t.also_known_as.join(", ")}
                    </p>
                  )}
                  {t.related_terms && t.related_terms.length > 0 && (
                    <p className="mt-1 text-xs text-rf-ink/50">
                      <span className="font-bold uppercase tracking-wider text-rf-ink/40">Related:</span>{" "}
                      {t.related_terms.map((rt, i) => {
                        const relSlug = (rt || "").toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                        return (
                          <span key={rt}>
                            <Link
                              to={`/kitchen-help/${relSlug}`}
                              className="underline decoration-dotted underline-offset-2 hover:text-rf-orange"
                            >
                              {rt}
                            </Link>
                            {i < t.related_terms.length - 1 ? ", " : ""}
                          </span>
                        );
                      })}
                    </p>
                  )}
                  <Link
                    to={`/kitchen-help/${t.slug}`}
                    className="mt-3 inline-block text-xs font-bold text-rf-orange hover:text-rf-ink"
                    data-testid={`kh-open-${t.slug}`}
                  >
                    Open full entry →
                  </Link>
                </article>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-16 text-center">
          <Link to="/dashboard" className="inline-flex items-center gap-2 px-6 py-3 bg-rf-orange text-white rounded-full font-bold hover:bg-rf-ink transition">
            Back to your kitchen
          </Link>
        </div>
      </section>
    </div>
  );
}
